import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import {
  AssignableRole,
  DashboardUser,
  DashboardUserPermissions,
  hasValidPermissions,
  isAssignableRole,
  warehousesFor
} from '@/domain/dashboard-user'
import {
  ContactType,
  EcommerceContact,
  MAX_CONTACT_NAME_LENGTH,
  MAX_CONTACT_PHONE_LENGTH,
  MAX_CONTACTS_PER_TYPE,
  PointOfContact,
  fullContactTypes,
  isDeliverableEmail
} from '@/domain/ecommerce-contact'
import { isValidEmail, isValidPhone } from '@/utils'
import {
  DEFAULT_ROLE,
  ROLE_OPTIONS,
  WarehouseOption,
  roleDescriptionKey,
  roleLabelKey,
  warehouseLabel
} from './user-roles'
import { ContactTypesSelect } from './contact-types-select'

const {
  Button,
  SelectCombobox,
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogTitle,
  DialogDescription,
  DialogCloseButton,
  DialogHeader,
  DialogFooter,
  Input
} = Base

export type SaveUserInput = DashboardUserPermissions & {
  id?: string
  email: string
  pointOfContact: PointOfContact
}

interface UserDialogProps {
  isOpen: boolean
  // Present when editing: the email is then fixed and only the permissions change.
  user?: DashboardUser
  // What the user is already the point of contact for, in the ecommerce on screen.
  pointOfContact?: PointOfContact
  // Every contact of the ecommerce, to keep a role from going past settings-api's limit.
  contacts: EcommerceContact[]
  warehouses: WarehouseOption[]
  saving: boolean
  onClose: () => void
  onSave: (input: SaveUserInput) => void
}

export const UserDialog: React.FC<UserDialogProps> = ({
  isOpen,
  user,
  pointOfContact,
  contacts,
  warehouses,
  saving,
  onClose,
  onSave
}) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  // A stored role the dashboard does not offer is left unselected rather than shown as the
  // default one: preselecting it would turn merely opening this dialog and saving into a
  // permission change the admin never asked for.
  const [role, setRole] = useState<AssignableRole | ''>(
    user ? storedRole(user) : DEFAULT_ROLE
  )
  const [assignedWarehouses, setAssignedWarehouses] = useState<string[]>(
    user?.assignedWarehouses ?? []
  )
  const [contactTypesPicked, setContactTypesPicked] = useState<ContactType[]>(
    pointOfContact?.types ?? []
  )
  const [contactName, setContactName] = useState(pointOfContact?.name ?? '')
  const [contactPhone, setContactPhone] = useState(pointOfContact?.phone ?? '')

  // Only a reviewer is warehouse-scoped, so the callback must not carry a list the new role
  // does not use; leaving it in would silently re-apply it on the next reviewer promotion.
  const permissions: DashboardUserPermissions | undefined =
    role === ''
      ? undefined
      : { role, assignedWarehouses: warehousesFor(role, assignedWarehouses) }
  const isReviewer = role === 'REVIEWER'
  const invalidEmail = !user && email.length > 0 && !isValidEmail(email)
  const validEmail = user ? true : email.length > 0 && !invalidEmail
  const isContact = contactTypesPicked.length > 0
  // Only a new user needs a topic: existing users from before topics existed can still be edited.
  const topicRequired = !user
  // settings-api refuses contact emails that parse but bounce, which isValidEmail lets through.
  const undeliverableEmail =
    isContact &&
    validEmail &&
    !isDeliverableEmail((user?.email ?? email).trim())
  const fullTypes = fullContactTypes(contacts, (user?.email ?? email).trim())
  // Checked before the user is saved, so settings-api does not refuse the contacts afterwards.
  const overCapacity = contactTypesPicked.some((type) =>
    fullTypes.includes(type)
  )
  const invalidPhone =
    contactPhone.trim().length > 0 && !isValidPhone(contactPhone.trim())
  const canSave =
    validEmail &&
    (isContact || !topicRequired) &&
    !invalidPhone &&
    !undeliverableEmail &&
    !overCapacity &&
    !!permissions &&
    hasValidPermissions(permissions)

  const handleSave = () => {
    if (!canSave || !permissions) return
    onSave({
      ...permissions,
      id: user?.id,
      email: user?.email ?? email,
      pointOfContact: {
        types: contactTypesPicked,
        name: isContact ? contactName.trim() : '',
        phone: isContact ? contactPhone.trim() : ''
      }
    })
  }

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogPopup>
          <div className="flex w-[27.5rem] max-w-full flex-col gap-4">
            <DialogCloseButton />
            <DialogHeader>
              <DialogTitle>
                {user
                  ? t('dashboard_users.edit_title')
                  : t('dashboard_users.invite_title')}
              </DialogTitle>
              <DialogDescription>
                {user
                  ? t('dashboard_users.edit_description')
                  : t('dashboard_users.invite_description')}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="inline-block text-sm" htmlFor="contact-name">
                  {t('dashboard_users.contact_name')}
                </label>
                <Input
                  id="contact-name"
                  name="contact-name"
                  autoComplete="off"
                  maxLength={MAX_CONTACT_NAME_LENGTH}
                  value={contactName}
                  onChange={(e) => setContactName(e.currentTarget.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="inline-block text-sm" htmlFor="contact-phone">
                  {t('dashboard_users.contact_phone')}
                </label>
                <Input
                  id="contact-phone"
                  name="contact-phone"
                  type="tel"
                  autoComplete="off"
                  maxLength={MAX_CONTACT_PHONE_LENGTH}
                  value={contactPhone}
                  aria-invalid={invalidPhone}
                  aria-describedby={
                    invalidPhone ? 'contact-phone-error' : undefined
                  }
                  onChange={(e) => setContactPhone(e.currentTarget.value)}
                />
                {invalidPhone ? (
                  <span
                    id="contact-phone-error"
                    className="pl-1 text-xs leading-none text-red-500"
                  >
                    {t('dashboard_users.invalid_phone')}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="inline-block text-sm" htmlFor="user-email">
                {t('dashboard_users.email')}
              </label>
              <Input
                id="user-email"
                name="user-email"
                type="email"
                autoComplete="off"
                placeholder="colleague@example.com"
                value={user?.email ?? email}
                disabled={!!user}
                aria-invalid={invalidEmail || undeliverableEmail}
                aria-describedby={
                  invalidEmail
                    ? 'user-email-error'
                    : undeliverableEmail
                      ? 'point-of-contact-email-error'
                      : undefined
                }
                onChange={(e) => setEmail(e.currentTarget.value)}
              />
              {invalidEmail ? (
                <span
                  id="user-email-error"
                  className="pl-1 text-xs leading-none text-red-500"
                >
                  {t('dashboard_users.invalid_email')}
                </span>
              ) : null}
            </div>

            <div className="flex flex-col gap-1">
              <label className="inline-block text-sm">
                {t('dashboard_users.role')}
              </label>
              <SelectCombobox<AssignableRole>
                items={ROLE_OPTIONS.map((option) => option.value)}
                value={role === '' ? null : role}
                onChange={(next) => setRole(next ?? '')}
                itemLabel={(value) => t(roleLabelKey(value))}
                placeholder={t('dashboard_users.select_role')}
                ariaLabel={t('dashboard_users.role')}
                triggerLabel={t('dashboard_users.select_role')}
                emptyText={t('dashboard_users.select_role')}
              />
              <p className="text-xs text-neutral-600">
                {role === ''
                  ? t('dashboard_users.role_unknown_hint')
                  : t(roleDescriptionKey(role))}
              </p>
            </div>

            {isReviewer ? (
              <div className="flex flex-col gap-1">
                <label className="inline-block text-sm">
                  {t('dashboard_users.warehouses')}
                </label>
                <SelectCombobox<string>
                  multiple
                  items={warehouses.map((warehouse) => warehouse.id)}
                  value={assignedWarehouses}
                  onChange={setAssignedWarehouses}
                  itemLabel={(id) => warehouseLabel(warehouses, id)}
                  placeholder={t('dashboard_users.select_warehouses')}
                  ariaLabel={t('dashboard_users.warehouses')}
                  triggerLabel={t('dashboard_users.select_warehouses')}
                  emptyText={t('dashboard_users.select_warehouses')}
                />
                {/* A reviewer with no warehouse sees no returns at all, so the API refuses it too. */}
                {permissions && !hasValidPermissions(permissions) ? (
                  <p className="text-xs text-error-main">
                    {t('dashboard_users.warehouses_required')}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-col gap-1">
              <label className="inline-block text-sm">
                {t('dashboard_users.point_of_contact')}
                {topicRequired ? (
                  <span className="text-error-main"> *</span>
                ) : null}
              </label>
              <ContactTypesSelect
                value={contactTypesPicked}
                onChange={setContactTypesPicked}
                disabledTypes={fullTypes}
                placeholder={
                  topicRequired ? t('dashboard_users.select_topics') : undefined
                }
                required={topicRequired}
              />
              <p className="text-xs text-neutral-600">
                {t('dashboard_users.point_of_contact_hint')}
              </p>
              {undeliverableEmail ? (
                <span
                  id="point-of-contact-email-error"
                  className="pl-1 text-xs leading-none text-red-500"
                >
                  {t('dashboard_users.point_of_contact_invalid_email')}
                </span>
              ) : null}
              {overCapacity ? (
                <p role="alert" className="text-xs text-error-main">
                  {t('dashboard_users.point_of_contact_full', {
                    max: MAX_CONTACTS_PER_TYPE
                  })}
                </p>
              ) : null}
            </div>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={onClose}>
                {t('dashboard_users.cancel')}
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={!canSave}
                loading={saving}
                onClick={handleSave}
              >
                {user
                  ? t('dashboard_users.save')
                  : t('dashboard_users.send_invite')}
              </Button>
            </DialogFooter>
          </div>
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  )
}

function storedRole(user: DashboardUser): AssignableRole | '' {
  return isAssignableRole(user.role) ? user.role : ''
}
